export interface Theme {
  id: string;
  name: string;
  background: string;
  primary: string;
  secondary: string;
  accent: string;
  text: string;
  surface: string;
  diceGradient: string;
  diceNumbers: string;
  diceShade: string;
  glow: string;
  particle: string;
  headerGradient: string;
  cardBackground: string;
  cardBorder: string;
  // Animation preferences
  animationSpeed?: 'fast' | 'normal' | 'slow';
  animationIntensity?: 'minimal' | 'moderate' | 'high';
  enableParticles?: boolean;
  enableSounds?: boolean;
  enableHaptics?: boolean;
}

export const themes: Record<string, Theme> = {
  default: {
    id: 'default',
    name: 'Classic',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    primary: '#4CAF50',
    secondary: '#2196F3',
    accent: '#FFD700',
    text: '#333',
    surface: '#fff',
    diceGradient: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
    diceNumbers: '#333',
    diceShade: 'rgba(0,0,0,0.1)',
    glow: '#FFD700',
    particle: '#4CAF50',
    headerGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBackground: '#fff',
    cardBorder: '#ddd'
  },
  
  neon: {
    id: 'neon',
    name: 'Neon Nights',
    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
    primary: '#00ff88',
    secondary: '#0099ff',
    accent: '#ff00ff',
    text: '#fff',
    surface: '#1a1a2e',
    diceGradient: 'linear-gradient(145deg, #0f0f23, #1a1a2e)',
    diceNumbers: '#00ff88',
    diceShade: 'rgba(0,255,136,0.3)',
    glow: '#ff00ff',
    particle: '#00ff88',
    headerGradient: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
    cardBackground: '#1a1a2e',
    cardBorder: '#00ff88'
  },
  
  vintage: {
    id: 'vintage',
    name: 'Vintage Casino',
    background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 50%, #CD853F 100%)',
    primary: '#DAA520',
    secondary: '#B8860B',
    accent: '#FFD700',
    text: '#2F4F2F',
    surface: '#F5DEB3',
    diceGradient: 'linear-gradient(145deg, #F5DEB3, #DEB887)',
    diceNumbers: '#8B4513',
    diceShade: 'rgba(139,69,19,0.2)',
    glow: '#FFD700',
    particle: '#DAA520',
    headerGradient: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
    cardBackground: '#F5DEB3',
    cardBorder: '#DAA520'
  },
  
  ocean: {
    id: 'ocean',
    name: 'Deep Ocean',
    background: 'linear-gradient(135deg, #000428 0%, #004e92 100%)',
    primary: '#00d4ff',
    secondary: '#0099cc',
    accent: '#00ffff',
    text: '#fff',
    surface: 'rgba(255,255,255,0.1)',
    diceGradient: 'linear-gradient(145deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
    diceNumbers: '#00d4ff',
    diceShade: 'rgba(0,212,255,0.3)',
    glow: '#00ffff',
    particle: '#00d4ff',
    headerGradient: 'linear-gradient(135deg, #000428 0%, #004e92 100%)',
    cardBackground: 'rgba(255,255,255,0.1)',
    cardBorder: '#00d4ff'
  },
  
  minimalist: {
    id: 'minimalist',
    name: 'Clean & Minimal',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    primary: '#495057',
    secondary: '#6c757d',
    accent: '#007bff',
    text: '#212529',
    surface: '#fff',
    diceGradient: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
    diceNumbers: '#495057',
    diceShade: 'rgba(73,80,87,0.1)',
    glow: '#007bff',
    particle: '#495057',
    headerGradient: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    cardBackground: '#fff',
    cardBorder: '#dee2e6'
  },
  
  sunset: {
    id: 'sunset',
    name: 'Sunset Paradise',
    background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
    primary: '#ff6b6b',
    secondary: '#4ecdc4',
    accent: '#ffe66d',
    text: '#2c3e50',
    surface: 'rgba(255,255,255,0.9)',
    diceGradient: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
    diceNumbers: '#2c3e50',
    diceShade: 'rgba(44,62,80,0.1)',
    glow: '#ffe66d',
    particle: '#ff6b6b',
    headerGradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    cardBackground: 'rgba(255,255,255,0.9)',
    cardBorder: '#ff6b6b'
  }
};

/**
 * Special-id för temapreferensen "följ systemet". Inte ett eget tema –
 * resolvas till AUTO_LIGHT_THEME/AUTO_DARK_THEME via prefers-color-scheme.
 */
export const AUTO_THEME_ID = 'auto';
const AUTO_LIGHT_THEME = 'default';
const AUTO_DARK_THEME = 'ocean';

class ThemeManager {
  private currentTheme: Theme = themes.default;
  /** Sparad preferens: ett tema-id eller AUTO_THEME_ID ("följ systemet"). */
  private preference: string = 'default';
  private listeners: ((theme: Theme) => void)[] = [];
  private initialized = false;

  constructor() {
    this.loadTheme();
    // CHANGE (2026-08-15): Auto-läge följer systemets ljus/mörk-inställning
    // och byter tema live när systemet växlar.
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      try {
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
          if (this.preference === AUTO_THEME_ID) {
            this.currentTheme = this.resolveAutoTheme();
            this.applyTheme();
            this.notifyListeners();
          }
        };
        if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
      } catch {}
    }
    // Applicera tema direkt vid konstruktion
    if (typeof document !== 'undefined') {
      this.applyTheme();
      this.initialized = true;
    }
  }

  private resolveAutoTheme(): Theme {
    try {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return themes[AUTO_DARK_THEME];
      }
    } catch {}
    return themes[AUTO_LIGHT_THEME];
  }

  private loadTheme(): void {
    try {
      const saved = localStorage.getItem('yatzy-theme');
      if (saved === AUTO_THEME_ID) {
        this.preference = AUTO_THEME_ID;
        this.currentTheme = this.resolveAutoTheme();
      } else if (saved && themes[saved]) {
        this.preference = saved;
        this.currentTheme = themes[saved];
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    }
  }

  private saveTheme(): void {
    try {
      localStorage.setItem('yatzy-theme', this.preference);
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  }

  getCurrentTheme(): Theme {
    return this.currentTheme;
  }

  /** Sparad preferens – tema-id eller AUTO_THEME_ID (för ThemeSelector-UI:t). */
  getPreference(): string {
    return this.preference;
  }

  setTheme(themeId: string): void {
    if (themeId === AUTO_THEME_ID) {
      this.preference = AUTO_THEME_ID;
      this.currentTheme = this.resolveAutoTheme();
      this.saveTheme();
      this.applyTheme();
      this.notifyListeners();
      return;
    }
    if (themes[themeId]) {
      this.preference = themeId;
      this.currentTheme = themes[themeId];
      this.saveTheme();
      this.applyTheme();
      this.notifyListeners();
    }
  }

  // Ny metod för att säkerställa tema-applicering
  ensureThemeApplied(): void {
    // Alltid kontrollera att CSS-variablerna är korrekta, oavsett initialized-flag
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      const currentBg = root.style.getPropertyValue('--theme-background');
      const expectedBg = this.currentTheme.background;
      
      // Om CSS-variablerna inte matchar det sparade temat, applicera temat igen
      if (currentBg !== expectedBg) {
        console.log('[ThemeManager] CSS variables mismatch detected, reapplying theme:', this.currentTheme.id);
        this.applyTheme();
      }
      
      if (!this.initialized) {
        this.initialized = true;
      }
    }
  }

  getAllThemes(): Theme[] {
    return Object.values(themes);
  }

  subscribe(listener: (theme: Theme) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentTheme));
  }

  // Animation and UI preferences getters
  public getAnimationSpeed(): number {
    const speed = this.currentTheme.animationSpeed || 'normal';
    switch (speed) {
      case 'fast': return 0.7;
      case 'slow': return 1.5;
      default: return 1.0; // normal
    }
  }

  public getAnimationIntensity(): number {
    const intensity = this.currentTheme.animationIntensity || 'moderate';
    switch (intensity) {
      case 'minimal': return 0.5;
      case 'high': return 1.5;
      default: return 1.0; // moderate
    }
  }

  public shouldEnableParticles(): boolean {
    return this.currentTheme.enableParticles !== false; // Default to true
  }

  public shouldEnableSounds(): boolean {
    return this.currentTheme.enableSounds !== false; // Default to true
  }

  public shouldEnableHaptics(): boolean {
    return this.currentTheme.enableHaptics !== false; // Default to true
  }

  public ensureThemeVariables(force = false): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sentinelVars = ['--theme-background', '--theme-card-background', '--felt-base-color'];
    let shouldReapply = force;
    if (!shouldReapply) {
      shouldReapply = sentinelVars.some(varName => {
        const inlineValue = root.style.getPropertyValue(varName);
        if (inlineValue && inlineValue.trim()) {
          return false;
        }
        try {
          const computed = getComputedStyle(root).getPropertyValue(varName);
          return !computed || !computed.trim() || computed.trim() === 'initial' || computed.trim() === 'inherit';
        } catch {
          return true;
        }
      });
    }

    if (shouldReapply) {
      this.applyTheme();
    }
  }

  private applyTheme(): void {
    // Kontrollera att document finns
    if (typeof document === 'undefined') return;
    
    // Aplicera tema-variabler till CSS root
    const root = document.documentElement;
    
    root.style.setProperty('--theme-background', this.currentTheme.background);
    root.style.setProperty('--theme-primary', this.currentTheme.primary);
    root.style.setProperty('--theme-secondary', this.currentTheme.secondary);
    root.style.setProperty('--theme-accent', this.currentTheme.accent);
    root.style.setProperty('--theme-text', this.currentTheme.text);
    root.style.setProperty('--theme-surface', this.currentTheme.surface);
    root.style.setProperty('--theme-dice-gradient', this.currentTheme.diceGradient);
    root.style.setProperty('--theme-dice-numbers', this.currentTheme.diceNumbers);
    root.style.setProperty('--theme-dice-shade', this.currentTheme.diceShade);
    root.style.setProperty('--theme-glow', this.currentTheme.glow);
    root.style.setProperty('--theme-particle', this.currentTheme.particle);
    root.style.setProperty('--theme-header-gradient', this.currentTheme.headerGradient);
    root.style.setProperty('--theme-card-background', this.currentTheme.cardBackground);
    root.style.setProperty('--theme-card-border', this.currentTheme.cardBorder);

    // Subtila, tema-anpassade färger för score-rader (avklarade/strukna)
    const lighten = (hex: string, percent: number) => {
      const h = hex.replace('#', '');
      const bigint = parseInt(h, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      const mix = (channel: number) => Math.round(channel + (255 - channel) * (percent / 100));
      const rr = mix(r).toString(16).padStart(2, '0');
      const gg = mix(g).toString(16).padStart(2, '0');
      const bb = mix(b).toString(16).padStart(2, '0');
      return `#${rr}${gg}${bb}`;
    };

    const primary = this.currentTheme.primary || '#4CAF50';
    const softPink = '#ff2d55';
    const completeBg = lighten(primary, 85); // väldigt ljus ton av tema-primary
    const completeBorder = lighten(primary, 70);
    const struckBg = lighten(softPink, 88);
    const struckBorder = lighten(softPink, 70);

    root.style.setProperty('--score-row-complete-bg', completeBg);
    root.style.setProperty('--score-row-complete-border', completeBorder);
    root.style.setProperty('--score-row-struck-bg', struckBg);
    root.style.setProperty('--score-row-struck-border', struckBorder);

    // Bonus hint background (soft secondary)
    try {
      const rgba = (hex: string, alpha: number) => {
        const h = hex.replace('#', '');
        const bigint = parseInt(h, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
      };
      root.style.setProperty('--bonus-hint-bg', rgba(this.currentTheme.secondary || '#2196F3', 0.08));
    } catch {}
    
    // Felt table colors derived from theme primary/secondary to allow dynamic scorecard felt
    try {
      const baseHex = this.currentTheme.primary || '#0f6d2f';
      const blend = (hex1: string, hex2: string, ratio: number) => {
        const h1 = hex1.replace('#','');
        const h2 = hex2.replace('#','');
        const b1 = parseInt(h1,16); 
        const b2 = parseInt(h2,16);
        const r1=(b1>>16)&255, g1=(b1>>8)&255, bg1=b1&255;
        const r2=(b2>>16)&255, g2=(b2>>8)&255, bg2=b2&255;
        const mix=(a:number,b:number)=>Math.round(a+(b-a)*ratio);
        const r=mix(r1,r2), g=mix(g1,g2), b=mix(bg1,bg2);
        const toHex=(n:number)=>n.toString(16).padStart(2,'0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      };
      const darken = (hex: string, percent: number) => {
        const h = hex.replace('#','');
        const bigint = parseInt(h,16);
        let r=(bigint>>16)&255; 
        let g=(bigint>>8)&255; 
        let b=bigint&255;
        const f = 1 - percent/100;
        r=Math.round(r*f); 
        g=Math.round(g*f); 
        b=Math.round(b*f);
        const toHex=(n:number)=>n.toString(16).padStart(2,'0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      };
      const base = baseHex;
      const dark = darken(base, 28);
      const light = blend(base, '#ffffff', 0.18);
      const edge = darken(base, 42);
      root.style.setProperty('--felt-base-color', base);
      root.style.setProperty('--felt-dark-color', dark);
      root.style.setProperty('--felt-light-color', light);
      root.style.setProperty('--felt-edge-color', edge);
    } catch {}
  }
}

export const themeManager = new ThemeManager();

// Exponera metod för att säkerställa tema vid mount
export const ensureThemeApplied = () => themeManager.ensureThemeApplied();

// Animation utility functions - use the class methods
export const getAnimationSpeed = (): number => themeManager.getAnimationSpeed();
export const getAnimationIntensity = (): number => themeManager.getAnimationIntensity();
export const shouldEnableParticles = (): boolean => themeManager.shouldEnableParticles();
export const shouldEnableSounds = (): boolean => themeManager.shouldEnableSounds();
export const shouldEnableHaptics = (): boolean => themeManager.shouldEnableHaptics();
